import React, { useEffect, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton";
import styles from "./index.module.scss";
import { Loader } from "@progress/kendo-react-indicators";

const LevelInterestGrid = ({
  onBandUpdate,
  onRatesUpdate,
  selectedScheme,
  loading,
}) => {
  const [schemeRates, setSchemeRates] = useState([]);
  const [bands, setBands] = useState([]);
  const [rates, setRates] = useState({});
  const [newRateIndices, setNewRateIndices] = useState(new Set());

  useEffect(() => {
    if (selectedScheme) {
      setSchemeRates(selectedScheme.schemeRates || []);
      setNewRateIndices(new Set());
      const bandArray = [];
      for (let i = 1; i <= 10; i++) {
        const bandValue = selectedScheme[`band${i}`] || 0;
        bandArray.push({
          level: i,
          bandKey: `band${i}`,
          threshold: bandValue,
        });
      }
      setBands(bandArray);
      const ratesObject = {};
      selectedScheme.schemeRates?.forEach((schemeRate, rateIndex) => {
        bandArray.forEach(band => {
          const rateKey = `level${band.level}Rate`;
          if (!ratesObject[band.level]) {
            ratesObject[band.level] = {};
          }
          ratesObject[band.level][rateIndex] = schemeRate[rateKey] || 0;
        });
      });
      setRates(ratesObject);
    }
  }, [selectedScheme]);

  const handleBandChange = (level, value) => {
    const updatedBands = bands.map(band => 
      band.level === level 
        ? { ...band, threshold: value }
        : band
    );
    setBands(updatedBands);
    if (onBandUpdate) {
      onBandUpdate(updatedBands);
    }
  };

  const handleRateChangeLocal = (level, rateIndex, value) => {
    const updatedRates = {
      ...rates,
      [level]: {
        ...rates[level],
        [rateIndex]: value
      }
    };
    setRates(updatedRates);
    const updatedSchemeRates = schemeRates.map((schemeRate, index) => {
      if (index === rateIndex) {
        return {
          ...schemeRate,
          [`level${level}Rate`]: value
        };
      }
      return schemeRate;
    });
    setSchemeRates(updatedSchemeRates);
    if (onRatesUpdate) {
      onRatesUpdate(updatedSchemeRates);
    }
  };

  const handleDateChange = (rateIndex, newDate) => {
    const updatedSchemeRates = schemeRates.map((rate, index) => 
      index === rateIndex 
        ? { ...rate, effectiveFrom: newDate.toISOString().split('T')[0] }
        : rate
    );
    setSchemeRates(updatedSchemeRates);
    if (onRatesUpdate) {
      onRatesUpdate(updatedSchemeRates);
    }
  };

  const handleNewRatesClick = () => {
    const newRateIndex = schemeRates.length;
    const newSchemeRate = {
      schemeRateId: null, 
      effectiveFrom: new Date().toISOString().split('T')[0],
      active: true,
      ...Object.fromEntries(
        Array.from({length: 10}, (_, i) => [`level${i+1}Rate`, 0])
      )
    };

    const updatedSchemeRates = [...schemeRates, newSchemeRate];
    setSchemeRates(updatedSchemeRates);
    
    setNewRateIndices(prev => new Set([...prev, newRateIndex]));
    
    const newRates = { ...rates };
    bands.forEach(band => {
      if (!newRates[band.level]) {
        newRates[band.level] = {};
      }
      newRates[band.level][newRateIndex] = 0;
    });
    setRates(newRates);

    if (onRatesUpdate) {
      onRatesUpdate(updatedSchemeRates);
    }
    
  };

  if (!selectedScheme || loading) {
    return (
      <div className={`d-flex flex-column bg-white ${styles['level-interest-container']}`}>
        <div className={`d-flex justify-content-between align-items-center ${styles['heading-text']} px-4 py-2`}>
          <div>Level Interest Rate</div>
          <Button
            className="common-btn bgBtnSecondary"
            onClick={handleNewRatesClick}
            size="small"
            style={{
              height: "30px",
              width: "100px",
              fontSize: "12px",
            }}
          >
            New Rates
          </Button>
        </div>
        <div className="p-4 text-center text-muted">
          {loading ?  <Loader
              size="medium"
              type="pulsing"
            />: "No scheme selected"}
        </div>
      </div>
    );
  }

  return (
    <div className={`d-flex flex-column bg-white ${styles['level-interest-container']}`}>
      <div className={`d-flex justify-content-between align-items-center ${styles['heading-text']} px-4 py-2`}>
        <div>Level Interest Rate</div>
        <Button
          className="common-btn bgBtnSecondary"
          onClick={handleNewRatesClick}
          size="small"
          style={{
            height: "30px",
            width: "100px",
            fontSize: "12px",
          }}
        >
          New Rates
        </Button>
      </div>

      <div className="px-4 py-1" style={{ width: '100%', overflow: 'hidden' }}>
        <div className={styles['grid-container']}>
          <div className={`d-flex gap-3 bg-white ${styles['grid-header']}`}>
            <div className={styles['header-cell']}></div>
            <div className={styles['header-cell']}></div>
            {schemeRates.map((schemeRate, index) => (
              <div key={index} className={`${styles['header-cell']}`}>
                Interest Rate
                <div className={styles['rate-date']}>
                  {newRateIndices.has(index) ? (
                    <div className="" style={{width: "100px"}}>
                      <DatePicker
                        value={schemeRate.effectiveFrom ? new Date(schemeRate.effectiveFrom) : new Date()}
                        onChange={(e) => handleDateChange(index, e.value)}
                        toggleButton={CalenderCustomToggleButton}
                        format="dd/MM/yyyy"
                      />
                    </div>
                  ) : (
                    `Effective From ${schemeRate.effectiveFrom ? 
                      new Date(schemeRate.effectiveFrom).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      }) : 'N/A'}`
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="d-flex flex-column py-2  " style={{minWidth: 'max-content'}}>
            {bands.map((band) => (
              <div 
                key={band.level} 
                className={`d-flex gap-3 ${band.level === 1 ? styles['first-row'] : ''} ${band.level === bands.length ? styles['last-row'] : ''}`}
                style={{minWidth: 'max-content'}}
             >
                <div className={`${styles['grid-cell']} fw-semibold`}>
                  Level {band.level}
                </div>

                <div className={`${styles['grid-cell']} right-align`}>
                  <NumericTextBox
                    value={band.threshold}
                    onChange={(e) => handleBandChange(band.level, e.value)}
                    format="n0"
                    style={{ width: "130px", height: "32px" }}
                    spinners={false}
                    placeholder="0.00"
                    className={styles['no-wheel-scroll']}
                    min={0}
                  />
                </div>
                
                {schemeRates.map((_, rateIndex) => (
                  <div key={rateIndex} className={`d-flex gap-2 right-align ${styles['grid-cell']}`}>
                    <NumericTextBox
                      value={rates[band.level]?.[rateIndex] || 0}
                      onChange={(e) => handleRateChangeLocal(band.level, rateIndex, e.value)}
                      format="n2"
                      className={styles['no-wheel-scroll']}
                      style={{ width: "130px", height: "32px" }}
                      spinners={false}
                      placeholder="0.00"
                      min={0}
                    />
                    %
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LevelInterestGrid;